import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
// import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Session from "../shared/Session";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Tooltip from "@mui/material/Tooltip";
import ReplayIcon from "@mui/icons-material/Replay";

//

// type Execution = {
//   id: string | number;
//   created_at?: string;
//   stdout?: string | null;
//   stderr?: string | null;
// };

// type Iteration = {
//   id: string | number;
//   created_at?: string;
//   plan?: string | null;
//   code?: string | null;
//   executions?: Execution[] | null;
// };

// type MiniProject = {
//   id: string | number;
//   name: string;
//   description?: string | null;
//   iterations?: Iteration[] | null;
// };

export default function MiniProjects() {
    const [miniProjects, setMiniProjects]: [any, any] = useState([]);
    const [loading, setLoading] = useState(true);

    // UI state for modals
    const [openModal, setOpenModal] = useState<{
        type: "code" | "plan" | "stdout" | "stderr" | null;
        title?: string;
        content?: string | null;
        open: boolean;
    }>({ type: null, open: false });

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function loadData() {
        setLoading(true);
        Session.api("GET", "/ai/mini_projects/", {}, (data: any) => {
            setMiniProjects(data);
            setLoading(false);
        });
    }

    function openTextModal(type: "code" | "plan" | "stdout" | "stderr", title: string, content?: string | null) {
        setOpenModal({ type, title, content: content ?? null, open: true });
    }
    function closeModal() {
        setOpenModal({ type: null, open: false });
    }

    function testIteration(iterationId: any) {
        setLoading(true);
        Session.api("POST", `/ai/mini_projects/${iterationId}/test/`, {}, (_data: any) => {
            loadData();
        });
    }

    function iterateProject(projectId: any) {
        setLoading(true);
        Session.api("POST", `/ai/mini_projects/${projectId}/iterate/`, {}, (_data: any) => {
            loadData();
        });
    }

    function deleteProject(projectId: any) {
        if (!window.confirm("Delete this project?")) return;
        setLoading(true);
        Session.api("DELETE", `/ai/mini_projects/${projectId}/`, {}, (_data: any) => {
            loadData();
        });
    }

    function deleteProjectIteration(iterationId: any) {
        if (!window.confirm("Delete this iteration?")) return;
        setLoading(true);
        Session.api("DELETE", `/ai/mini_projects/iterations/${iterationId}/`, {}, (_data: any) => {
            loadData();
        });
    }

    // Aggregate executions across all iterations and projects
    const executions = useMemo(() => {
        const list: Array<{
            projectId?: string | number;
            projectName?: string;
            iterationId?: string | number;
            iterationCreated?: string | undefined;
            execution: any;
            iterationPlan?: string | null;
            iterationCode?: string | null;
        }> = [];

        for (const project of miniProjects || []) {
            const projectName = project.name;
            for (const iter of project.iterations || []) {
                for (const exec of iter.executions || []) {
                    list.push({
                        projectId: project.id,
                        projectName,
                        iterationId: iter.id,
                        iterationCreated: iter.created_at,
                        execution: exec,
                        iterationPlan: iter.plan ?? null,
                        iterationCode: iter.code ?? null,
                    });
                }
            }
        }

        // sort by execution.created_at desc (most recent first)
        list.sort((a, b) => {
            const da = a.execution.created_at ? Date.parse(a.execution.created_at) : 0;
            const db = b.execution.created_at ? Date.parse(b.execution.created_at) : 0;
            return db - da;
        });

        return list;
    }, [miniProjects]);

    // Projects sorted by most recent iteration.created_at
    const projectsSorted = useMemo(() => {
        const copy = (miniProjects || []).slice();
        copy.sort((a: any, b: any) => {
            const maxA =
                (a.iterations || [])
                    .map((it: any) => (it.created_at ? Date.parse(it.created_at) : 0))
                    .reduce((mx: any, v: any) => Math.max(mx, v), 0) || 0;
            const maxB =
                (b.iterations || [])
                    .map((it: any) => (it.created_at ? Date.parse(it.created_at) : 0))
                    .reduce((mx: any, v: any) => Math.max(mx, v), 0) || 0;
            return maxB - maxA;
        });
        return copy;
    }, [miniProjects]);

    return (<>
        <Typography variant="h4" align="left" gutterBottom component="h4">
            AI Mini Projects
        </Typography>
        <Button variant="contained" color="primary" style={{ margin: "0.3%" }} onClick={() => Session.navigate(`/ai/mini_projects/new?redirect_to=/ai/mini_projects`)}>Create New</Button>
        <br />
        <Button variant="contained" color="primary" style={{ margin: "0.3%" }} onClick={loadData} disabled={loading}>Refresh</Button>
        <Box padding={2}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    {projectsSorted.length === 0 ? (
                        <Typography color="textSecondary">No projects available.</Typography>
                    ) : (
                        projectsSorted.map((p: any, index: number) => {
                            const latestIteration = (p.iterations || []).slice().sort((a: any, b: any) => {
                                const da = a.created_at ? Date.parse(a.created_at) : 0;
                                const db = b.created_at ? Date.parse(b.created_at) : 0;
                                return db - da;
                            })[0];
                            const subtitle = latestIteration ? `Latest iter: ${new Date(latestIteration.created_at ?? 0).toLocaleString()}` : "No iterations";
                            return (
                                <Accordion defaultExpanded={index === 0} key={p.id} style={{ marginBottom: 8 }}>
                                    <AccordionSummary
                                        style={{ minHeight: "50px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        <Box>
                                            <Typography style={{ lineHeight: 1 }} variant="subtitle1">{p.name}</Typography>
                                            <Typography style={{ lineHeight: 1 }} variant="caption" color="textSecondary">
                                                {subtitle}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" style={{ marginLeft: "15px" }} gap={1}>
                                            {p.iterations && p.iterations.length > 0 ? (
                                                <Tooltip title="Test Lastest Iteration">
                                                    <IconButton disabled={loading} size="small" onClick={(e: any) => { e.stopPropagation(); testIteration(p.iterations[0].id); }} >
                                                        <PlayArrowIcon style={{ color: "green" }} fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography style={{ lineHeight: 1 }} variant="caption" color="textSecondary">
                                                    No Iteration
                                                </Typography>
                                            )}
                                            <Tooltip title="Iterate Project">
                                                <IconButton size="small" style={{ margin: "3px !important" }} onClick={(e) => { e.stopPropagation(); iterateProject(p.id); }}>
                                                    <ReplayIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Forever">
                                                <IconButton size="small" style={{ margin: "3px !important" }} onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }}>
                                                    <DeleteForeverIcon color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ display: "block" }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "normal" }}>
                                            {p.description}
                                        </Typography>

                                        <Box mt={1}>
                                            {(p.iterations || []).length === 0 ? (
                                                <Typography variant="body2" color="textSecondary">
                                                    No iterations
                                                </Typography>
                                            ) : (
                                                (p.iterations || []).map((it: any, index: number) => (
                                                    <Accordion defaultExpanded={index === 0} key={p.id} style={{ marginBottom: 8 }}>
                                                        <AccordionSummary
                                                            style={{ minHeight: "50px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                                            expandIcon={<ExpandMoreIcon />}
                                                        >
                                                            <Box key={it.id} mb={1} p={1} borderRadius={1} style={{ border: "1px solid #eee" }}>
                                                                <Typography noWrap variant="subtitle2">Iteration {it.id}</Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {it.dt_created ? new Date(it.dt_created).toLocaleString() : ""}
                                                                </Typography>
                                                                <Tooltip title="Delete Iteration Forever">
                                                                    <IconButton size="small" style={{ margin: "3px !important" }} onClick={(e) => { e.stopPropagation(); deleteProjectIteration(it.id); }}>
                                                                        <DeleteForeverIcon color="error" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails style={{ display: "block" }}>
                                                            <Typography noWrap variant="caption">{it.description}</Typography>
                                                            <Box mb={1}>
                                                                <Button disabled={loading} size="small" onClick={() => openTextModal("plan", `${p.name} — Iter ${it.id} — Plan`, it.plan)} style={{ marginTop: 6 }}>
                                                                    View Plan
                                                                </Button>
                                                                <br />
                                                                <Button disabled={loading} size="small" onClick={() => openTextModal("plan", `${p.name} — Iter ${it.id} — Plan`, it.code)} style={{ marginTop: 6 }}>
                                                                    View Code
                                                                </Button>
                                                                <br />
                                                                <IconButton disabled={loading} size="small" onClick={(e: any) => { e.stopPropagation(); testIteration(it.id); }} title="Test iteration">
                                                                    <PlayArrowIcon style={{ color: "green" }} fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                                                                {(it.executions || []).length === 0 ? (
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        No executions
                                                                    </Typography>
                                                                ) : (
                                                                    (it.executions || []).map((ex: any) => (
                                                                        <Box key={ex.id} display="flex" alignItems="center" gap={1}>
                                                                            <Button disabled={loading} size="small" onClick={() => openTextModal("stdout", `Project ${p.name} • Exec ${ex.id} — Stdout`, ex.stdout ?? "")}>
                                                                                Stdout
                                                                            </Button>
                                                                            {ex.stderr ? (
                                                                                <Button disabled={loading} size="small" color="error" onClick={() => openTextModal("stderr", `Project ${p.name} • Exec ${ex.id} — Stderr`, ex.stderr ?? "")}>
                                                                                    Stderr
                                                                                </Button>
                                                                            ) : (
                                                                                <Button size="small" disabled>
                                                                                    No Errors ✅
                                                                                </Button>
                                                                            )}
                                                                        </Box>
                                                                    ))
                                                                )}
                                                            </Box>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                ))
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="h5">Executions</Typography>
                    </Box>

                    {executions.length === 0 ? (
                        <Typography color="textSecondary">No executions yet.</Typography>
                    ) : (
                        <List>
                            {executions.map((item, index) => {
                                const exec = item.execution;
                                const summaryTitle = `${item.projectName} — Exec ${exec.id}`;
                                const created = exec.dt_created ? new Date(exec.dt_created).toLocaleString() : "unknown";
                                return (
                                    <ListItem key={`${item.projectId}-${item.iterationId}-${exec.id}`} disableGutters style={{ paddingLeft: 0, paddingRight: 0 }}>
                                        <Accordion defaultExpanded={index === 0} style={{ width: "100%" }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box display="flex" flexDirection="column">
                                                    <Typography variant="subtitle1">{summaryTitle}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Iteration: {item.iterationId ?? "—"}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Executed At: {created}
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails style={{ display: "block" }}>
                                                <Box>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Execution Output:
                                                    </Typography>
                                                    <Box gap={1} mt={1}>
                                                        <Button size="small" variant="outlined" onClick={() => openTextModal("stdout", `${summaryTitle} — Stdout`, exec.stdout ?? "")}>
                                                            View Stdout
                                                        </Button>
                                                        <br />
                                                        <br />
                                                        {exec.stderr ? (
                                                            <Button size="small" variant="outlined" color="error" onClick={() => openTextModal("stderr", `${summaryTitle} — Stderr`, exec.stderr ?? "")}>
                                                                View Stderr
                                                            </Button>
                                                        ) : (
                                                            <Button size="small" variant="text" disabled>
                                                                No Errors ✅
                                                            </Button>
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                                                        {exec.stdout ? `Stdout length: ${exec.stdout.length}` : "No stdout"}
                                                    </Typography>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Grid>

            </Grid>

            <Dialog open={openModal.open} onClose={closeModal} maxWidth="md" fullWidth>
                <DialogTitle>{openModal.title}</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        fullWidth
                        minRows={8}
                        value={openModal.content ?? ""}
                        variant="outlined"
                        InputProps={{ readOnly: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box >
    </>);
}